import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../../api/axios'
import endpoints from '../../../api/endpoints'
import { extractApiErrorMessage } from '../../../utils/apiError'
import type { RootState } from '../../store'
import type { AvailableEmployeeSearchParams, AvailableEmployeeRecord } from './approverProxySlice'

export type { AvailableEmployeeSearchParams } from './approverProxySlice'
export type { AvailableEmployeeRecord } from './approverProxySlice'

// ============================================================================
// TYPES
// ============================================================================

/** Search mode options matching the proxy search radio buttons.
 * Note: the 'group' UI mode maps to 'approvalGrpId' in the API param.
 */
export type RequestorProxySearchOption = 'nuid' | 'name' | 'approvalGrpId'

/**
 * Payload shape for the requestor proxy search thunk.
 * - `searchOption='name'`  → provide `searchFName` and/or `searchLName`
 * - `searchOption='nuid'`  → provide `searchValue` (the NUID)
 * - `searchOption='group'` → provide `searchValue` (the Group ID)
 */
export interface RequestorProxySearchParams {
  searchOption: RequestorProxySearchOption
  /** The NUID or Group ID; used when searchOption is 'nuid' or 'group'. */
  searchValue?: string
  /** First name; used when searchOption is 'name'. */
  searchFName?: string
  /** Last name; used when searchOption is 'name'. */
  searchLName?: string
}

/**
 * Shape of a single record returned by the BFF requestor-proxy search API.
 * Fields match the actual API response keys.
 */
export interface RequestorProxyRecord {
  /** Approval group identifier, e.g. "00000894". */
  approvalGrpId: string
  /** Full approver name and NUID, e.g. "CAMPOS, BRIAN C (Y291587)". */
  approver: string
  /** Geographic region name, e.g. "Northern California". */
  region: string
  /** Single-character region code, e.g. "A". */
  regionCode: string
  /** Department name and code. */
  department: string
  /** Semicolon-delimited list of requestor proxy names/NUIDs. May be empty string when no proxies assigned. */
  reqstorProxy: string
  [key: string]: unknown
}

/**
 * Shape of a single proxy record returned by the fetchCurrentRP API.
 */
export interface CurrentProxyRecord {
  /** Display label — full name and NUID, e.g. "Test_LN, Test_FN sq (X034964)". */
  label: string
  /** NUID of the proxy employee. */
  value: string
  /** Region code. */
  region: string
  /** Department name. */
  department: string
  /** Proxy email address. */
  email: string
  /** Proxy start date (ISO 8601). */
  startDate: string
  /** Proxy end date (ISO 8601), or null if open-ended. */
  endDate: string | null
}

/** State shape for the requestorProxy slice. */
export interface RequestorProxyState {
  /** True while the search API call is in-flight. */
  isLoading: boolean
  /** True once the user has submitted at least one search — controls empty-state visibility. */
  searchPerformed: boolean
  /** Search results returned by the API. */
  data: RequestorProxyRecord[]
  /** Human-readable error message when the API call fails; null otherwise. */
  error: string | null
  /** True while the fetchCurrentRP API call is in-flight. */
  isLoadingFetchCurrent: boolean
  /** Current proxy records fetched on Edit click. */
  currentProxies: CurrentProxyRecord[]
  /** Error from the fetchCurrentRP call, or null. */
  fetchCurrentError: string | null
  /** True while the searchAvailableEmployeesRP API call is in-flight. */
  isLoadingEmployeeSearch: boolean
  /** True once an employee search has been submitted — controls empty-state visibility. */
  employeeSearchPerformed: boolean
  /** Eligible employees returned by the most recent RP employee search. */
  employeeResults: AvailableEmployeeRecord[]
  /** Error from the searchAvailableEmployeesRP call, or null. */
  employeeSearchError: string | null
  /** True while the assignRequestorProxies API call is in-flight. */
  isLoadingSave: boolean
  /** Error from the most recent failed assignRequestorProxies call, or null. */
  saveError: string | null
}

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState: RequestorProxyState = {
  isLoading: false,
  searchPerformed: false,
  data: [],
  error: null,
  isLoadingFetchCurrent: false,
  currentProxies: [],
  fetchCurrentError: null,
  isLoadingEmployeeSearch: false,
  employeeSearchPerformed: false,
  employeeResults: [],
  employeeSearchError: null,
  isLoadingSave: false,
  saveError: null,
}

// ============================================================================
// ASYNC THUNK
// ============================================================================

/**
 * Searches for requestor proxy records via the BFF search endpoint.
 *
 * GET /tort/requestor-proxy/searchRP
 *
 * Query params built from the payload:
 * - Always:  actionCode=searchRP, searchOption=<mode>
 * - name:    searchFName=<first>, searchLName=<last>
 * - nuid|group: searchValue=<input>
 *
 * @param payload - Search option and corresponding field values.
 * @returns Array of matching {@link RequestorProxyRecord} objects.
 */
export const searchRequestorProxy = createAsyncThunk<
  RequestorProxyRecord[],
  RequestorProxySearchParams
>(
  'admin/requestorProxy/search',
  async (payload, { rejectWithValue, signal }) => {
    try {
      const params: Record<string, string> = {
        actionCode: 'searchRP',
        searchOption: payload.searchOption,
      }

      if (payload.searchOption === 'name') {
        // Pass first/last name separately so the API can match on either
        if (payload.searchFName) params.searchFName = payload.searchFName
        if (payload.searchLName) params.searchLName = payload.searchLName
      } else if (payload.searchValue) {
        // Single value for nuid or group searches
        params.searchValue = payload.searchValue
      }

      const response = await api.get<RequestorProxyRecord[]>(
        endpoints.admin.searchRequestorProxy,
        { params, signal }
      )
      return response.data
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiErrorMessage(error, 'Failed to search requestor proxy records')
      )
    }
  }
)

/**
 * Searches for employees eligible to be added as a requestor/float proxy for a group.
 *
 * GET /tort/requestor-proxy/searchAvailableEmployees
 *
 * Query params:
 * - Always: `actionCode=empRPSearch`, `approvalGrpId=<digits>`, `searchOption=<mode>`
 * - name:   `searchLName=<last>` (required, min 2), `searchFName=<first>` (optional)
 * - nuid:   `searchValue=<nuid>`
 *
 * The `x-nuid` identity header is attached by the shared Axios interceptor.
 *
 * @param payload - Approval group, search option, and corresponding field values.
 * @returns Array of matching {@link AvailableEmployeeRecord} objects (empty when none).
 */
export const searchAvailableEmployeesRP = createAsyncThunk<
  AvailableEmployeeRecord[],
  AvailableEmployeeSearchParams
>(
  'admin/requestorProxy/searchAvailableEmployees',
  async (payload, { rejectWithValue, signal }) => {
    try {
      const params: Record<string, string> = {
        actionCode: 'empRPSearch',
        approvalGrpId: payload.approvalGrpId,
        searchOption: payload.searchOption,
      }

      if (payload.searchOption === 'name') {
        if (payload.searchLName) params.searchLName = payload.searchLName
        if (payload.searchFName) params.searchFName = payload.searchFName
      } else if (payload.searchValue) {
        params.searchValue = payload.searchValue
      }

      const response = await api.get<AvailableEmployeeRecord[]>(
        endpoints.admin.searchAvailableEmployeesRP,
        { params, signal }
      )
      return response.data
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiErrorMessage(error, 'Failed to search available employees')
      )
    }
  }
)

/**
 * Payload shape for the assignRequestorProxies thunk.
 */
export interface AssignRequestorProxiesPayload {
  /** The approval group to update. */
  approvalGrpId: string
  /** NUIDs of all current proxy members after the user's edits. */
  currentProxies: string[]
}

/**
 * Saves the current requestor-proxy assignments for an approval group.
 *
 * POST /tort/requestor-proxy/assignRequestorProxies
 *
 * @param payload - The approval group ID and the final list of proxy NUIDs.
 * @returns void on success.
 */
export const assignRequestorProxies = createAsyncThunk<
  void,
  AssignRequestorProxiesPayload
>(
  'admin/requestorProxy/assign',
  async (payload, { rejectWithValue }) => {
    try {
      await api.post(
        endpoints.admin.assignRequestorProxies,
        {
          actionCode: 'submitRP',
          approvalGrpId: payload.approvalGrpId,
          currentProxies: payload.currentProxies,
        }
      )
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiErrorMessage(error, 'Failed to save requestor proxy assignments')
      )
    }
  }
)

/**
 * Fetches the current proxy assignments for a given approval group.
 *
 * GET /tort/requestor-proxy/fetchCurrentRP
 *
 * Query params: `approvalGrpId=<id>&actionCode=fetchCurrentRP`
 *
 * @param approvalGrpId - The approval group ID to fetch proxies for.
 * @returns Array of {@link CurrentProxyRecord} objects for that group.
 */
export const fetchCurrentRequestorProxies = createAsyncThunk<
  CurrentProxyRecord[],
  string
>(
  'admin/requestorProxy/fetchCurrent',
  async (approvalGrpId, { rejectWithValue, signal }) => {
    try {
      const response = await api.get<CurrentProxyRecord[]>(
        endpoints.admin.fetchCurrentRequestorProxies,
        {
          params: { approvalGrpId, actionCode: 'fetchCurrentRP' },
          signal,
        }
      )
      return response.data
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiErrorMessage(error, 'Failed to fetch current proxy records')
      )
    }
  }
)

// ============================================================================
// SLICE
// ============================================================================

const requestorProxySlice = createSlice({
  name: 'requestorProxy',
  initialState,
  reducers: {
    /**
     * Resets the slice to its initial state.
     * Call this when navigating away from the proxy search page
     * to avoid stale results appearing on re-entry.
     */
    clearRequestorProxyState: (state) => {
      state.isLoading = false
      state.searchPerformed = false
      state.data = []
      state.error = null
      state.isLoadingFetchCurrent = false
      state.currentProxies = []
      state.fetchCurrentError = null
      state.isLoadingEmployeeSearch = false
      state.employeeSearchPerformed = false
      state.employeeResults = []
      state.employeeSearchError = null
      state.isLoadingSave = false
      state.saveError = null
    },
  },
  extraReducers: (builder) => {
    // ── searchRequestorProxy ────────────────────────────────────────────────
    builder.addCase(searchRequestorProxy.pending, (state) => {
      state.isLoading = true
      state.searchPerformed = true
      state.error = null
      state.data = []
      // Clear stale edit-panel errors and employee search results so they do
      // not persist across searches.
      state.saveError = null
      state.employeeSearchError = null
      state.employeeResults = []
      state.employeeSearchPerformed = false
    })
    builder.addCase(searchRequestorProxy.fulfilled, (state, action) => {
      state.isLoading = false
      state.data = action.payload
    })
    builder.addCase(searchRequestorProxy.rejected, (state, action) => {
      state.isLoading = false
      state.error = action.payload as string
    })
    // ── fetchCurrentRequestorProxies ────────────────────────────────────────
    builder.addCase(fetchCurrentRequestorProxies.pending, (state) => {
      state.isLoadingFetchCurrent = true
      state.fetchCurrentError = null
      state.currentProxies = []
    })
    builder.addCase(fetchCurrentRequestorProxies.fulfilled, (state, action) => {
      state.isLoadingFetchCurrent = false
      state.currentProxies = action.payload
    })
    builder.addCase(fetchCurrentRequestorProxies.rejected, (state, action) => {
      state.isLoadingFetchCurrent = false
      state.fetchCurrentError = action.payload as string
    })
    // ── searchAvailableEmployeesRP ──────────────────────────────────────────
    builder.addCase(searchAvailableEmployeesRP.pending, (state) => {
      state.isLoadingEmployeeSearch = true
      state.employeeSearchPerformed = true
      state.employeeSearchError = null
      state.employeeResults = []
    })
    builder.addCase(searchAvailableEmployeesRP.fulfilled, (state, action) => {
      state.isLoadingEmployeeSearch = false
      state.employeeResults = action.payload
    })
    builder.addCase(searchAvailableEmployeesRP.rejected, (state, action) => {
      state.isLoadingEmployeeSearch = false
      state.employeeSearchError = action.payload as string
    })
    // ── assignRequestorProxies ──────────────────────────────────────────────
    builder.addCase(assignRequestorProxies.pending, (state) => {
      state.isLoadingSave = true
      state.saveError = null
    })
    builder.addCase(assignRequestorProxies.fulfilled, (state) => {
      state.isLoadingSave = false
    })
    builder.addCase(assignRequestorProxies.rejected, (state, action) => {
      state.isLoadingSave = false
      state.saveError = action.payload as string
    })
  },
})

export const { clearRequestorProxyState } = requestorProxySlice.actions

// ============================================================================
// SELECTORS
// ============================================================================

/** True while the requestor proxy search API call is in-flight. */
export const selectRequestorProxyLoading = (state: RootState): boolean =>
  state.requestorProxy.isLoading

/** True once the user has performed at least one search. */
export const selectRequestorProxySearchPerformed = (state: RootState): boolean =>
  state.requestorProxy.searchPerformed

/** Search results from the most recent requestor proxy search. */
export const selectRequestorProxyData = (state: RootState): RequestorProxyRecord[] =>
  state.requestorProxy.data

/** Error message from the most recent failed requestor proxy search, or null. */
export const selectRequestorProxyError = (state: RootState): string | null =>
  state.requestorProxy.error

/** True while the fetchCurrentRP API call is in-flight. */
export const selectFetchCurrentLoading = (state: RootState): boolean =>
  state.requestorProxy.isLoadingFetchCurrent

/** Current proxy records fetched by the most recent fetchCurrentRP call. */
export const selectCurrentProxies = (state: RootState): CurrentProxyRecord[] =>
  state.requestorProxy.currentProxies

/** Error message from the most recent failed fetchCurrentRP call, or null. */
export const selectFetchCurrentError = (state: RootState): string | null =>
  state.requestorProxy.fetchCurrentError

/** True while the searchAvailableEmployeesRP API call is in-flight. */
export const selectRPAvailableEmployeeLoading = (state: RootState): boolean =>
  state.requestorProxy.isLoadingEmployeeSearch

/** True once an RP employee search has been submitted. */
export const selectRPAvailableEmployeeSearchPerformed = (state: RootState): boolean =>
  state.requestorProxy.employeeSearchPerformed

/** Employees returned by the most recent RP employee search. */
export const selectRPAvailableEmployeeResults = (state: RootState): AvailableEmployeeRecord[] =>
  state.requestorProxy.employeeResults

/** Error from the most recent failed RP employee search, or null. */
export const selectRPAvailableEmployeeError = (state: RootState): string | null =>
  state.requestorProxy.employeeSearchError

/** True while the assignRequestorProxies API call is in-flight. */
export const selectRPSaveLoading = (state: RootState): boolean =>
  state.requestorProxy.isLoadingSave

/** Error from the most recent failed assignRequestorProxies call, or null. */
export const selectRPSaveError = (state: RootState): string | null =>
  state.requestorProxy.saveError

export default requestorProxySlice.reducer
